// ===========================================
// BUSTED! - TABLE LAYOUT & ANIMATION ENDPOINTS
// ===========================================
// Defines the layout system for table elements and
// animation endpoints. This is the single source of
// truth for all position-based animations.
//
// DESIGN PRINCIPLES:
// 1. Server-authoritative: All clients receive the same layout
// 2. Game-agnostic: Endpoints are generic, games define which they need
// 3. Extensible: New games can define new endpoint types
// 4. DEV CONTROLS friendly: Admins can modify and broadcast changes
// ===========================================
// ===========================================
// BLACKJACK-SPECIFIC ENDPOINT BUILDERS
// ===========================================
/**
 * Standard endpoint IDs for Blackjack.
 * Using a consistent naming convention makes lookups predictable.
 */
export const BlackjackEndpointIds = {
    // Seat positions (visual positions 1-7)
    seat: (visualPos) => `seat-${visualPos}`,
    // Kitty (bet chip stack) for each seat
    kitty: (visualPos) => `seat-${visualPos}-kitty`,
    // Insurance chip stack for each seat
    insurance: (visualPos) => `seat-${visualPos}-insurance`,
    // Tray chip positions (one per denomination)
    trayChip: (denomination) => `tray-chip-${denomination}`,
    // Rail chip positions (one per denomination)
    railChip: (denomination) => `rail-chip-${denomination}`,
    // Staged bet area
    stagedBet: 'staged-bet',
    // Shoe position (card source)
    shoe: 'shoe',
    // Dealer area
    dealer: 'dealer',
    dealerCards: 'dealer-cards',
    // Tray container (parent for tray chips)
    tray: 'tray',
};
/**
 * Generates a complete Blackjack table layout from config.
 * This is the factory function that creates all endpoints.
 */
export function createBlackjackLayout(config) {
    const endpoints = {};
    const allSeats = [];
    const allKitties = [];
    const allInsurance = [];
    const allTrayChips = [];
    const allRailChips = [];
    // Create seat endpoints
    for (let i = 1; i <= config.seatCount; i++) {
        const seatPos = config.seatPositions[i];
        if (!seatPos)
            continue;
        const seatId = BlackjackEndpointIds.seat(i);
        endpoints[seatId] = {
            id: seatId,
            position: { x: seatPos.x, y: seatPos.y },
            scale: 1,
            rotation: seatPos.rotation,
            category: 'seat',
            meta: { visualPosition: i },
        };
        allSeats.push(seatId);
        // Kitty endpoint (child of seat)
        const kittyId = BlackjackEndpointIds.kitty(i);
        const kittyOffset = config.kittyOffsetPerSeat?.[i] || config.kittyOffset;
        endpoints[kittyId] = {
            id: kittyId,
            position: {
                x: seatPos.x + kittyOffset.x,
                y: seatPos.y + kittyOffset.y
            },
            scale: config.kittyScale,
            rotation: seatPos.rotation,
            category: 'kitty',
            parentId: seatId,
            meta: { visualPosition: i },
        };
        allKitties.push(kittyId);
        // Insurance endpoint (child of seat)
        const insuranceId = BlackjackEndpointIds.insurance(i);
        const insuranceOffset = config.insuranceOffsetPerSeat?.[i] || config.insuranceOffset;
        endpoints[insuranceId] = {
            id: insuranceId,
            position: {
                x: seatPos.x + insuranceOffset.x,
                y: seatPos.y + insuranceOffset.y
            },
            scale: config.insuranceScale,
            rotation: seatPos.rotation,
            category: 'insurance',
            parentId: seatId,
            meta: { visualPosition: i },
        };
        allInsurance.push(insuranceId);
    }
    // Create tray endpoint
    const trayId = BlackjackEndpointIds.tray;
    endpoints[trayId] = {
        id: trayId,
        position: config.trayPosition,
        scale: config.trayScale,
        rotation: config.trayRotation,
        category: 'tray',
    };
    // Create tray chip endpoints (children of tray)
    for (const [denomStr, pos] of Object.entries(config.trayChipPositions)) {
        const denom = Number(denomStr);
        const chipId = BlackjackEndpointIds.trayChip(denom);
        endpoints[chipId] = {
            id: chipId,
            position: {
                x: config.trayPosition.x + pos.x * config.trayScale,
                y: config.trayPosition.y + pos.y * config.trayScale,
            },
            scale: config.trayScale,
            rotation: config.trayRotation,
            category: 'tray',
            parentId: trayId,
            meta: { denomination: denom },
        };
        allTrayChips.push(chipId);
    }
    // Create rail chip endpoints
    for (const [denomStr, pos] of Object.entries(config.railChipPositions)) {
        const denom = Number(denomStr);
        const chipId = BlackjackEndpointIds.railChip(denom);
        endpoints[chipId] = {
            id: chipId,
            position: {
                x: config.railPosition.x + pos.x * config.railScale,
                y: config.railPosition.y + pos.y * config.railScale,
            },
            scale: config.railScale,
            rotation: 0,
            category: 'rail',
            meta: { denomination: denom },
        };
        allRailChips.push(chipId);
    }
    // Staged bet endpoint
    const stagedId = BlackjackEndpointIds.stagedBet;
    endpoints[stagedId] = {
        id: stagedId,
        position: config.stagedBetPosition,
        scale: config.stagedBetScale,
        rotation: 0,
        category: 'staged',
    };
    // Shoe endpoint
    const shoeId = BlackjackEndpointIds.shoe;
    endpoints[shoeId] = {
        id: shoeId,
        position: config.shoePosition,
        scale: config.shoeScale,
        rotation: config.shoeRotation,
        category: 'shoe',
    };
    // Dealer endpoint
    const dealerId = BlackjackEndpointIds.dealer;
    endpoints[dealerId] = {
        id: dealerId,
        position: config.dealerPosition,
        scale: 1,
        rotation: 0,
        category: 'dealer',
    };
    return {
        version: 1,
        canvasSize: { width: 1920, height: 1080 },
        gameType: 'blackjack',
        endpoints,
        groups: {
            'all-seats': allSeats,
            'all-kitties': allKitties,
            'all-insurance': allInsurance,
            'all-tray-chips': allTrayChips,
            'all-rail-chips': allRailChips,
        },
        meta: {
            updatedAt: Date.now(),
        },
    };
}
// ===========================================
// LAYOUT QUERIES
// ===========================================
/**
 * Helper functions for querying layout endpoints.
 * These provide type-safe access to common endpoint patterns.
 */
export const LayoutQueries = {
    /**
     * Get an endpoint by ID, or undefined if not found.
     */
    getEndpoint(layout, id) {
        return layout.endpoints[id];
    },
    /**
     * Get all endpoints in a group.
     */
    getGroup(layout, groupId) {
        const ids = layout.groups[groupId] || [];
        return ids
            .map(id => layout.endpoints[id])
            .filter((ep) => ep !== undefined);
    },
    /**
     * Get all endpoints in a category.
     */
    getByCategory(layout, category) {
        return Object.values(layout.endpoints).filter(ep => ep.category === category);
    },
    /**
     * Get endpoints by metadata value.
     * E.g., getByMeta(layout, 'denomination', 100) -> tray-chip-100, rail-chip-100
     */
    getByMeta(layout, key, value) {
        return Object.values(layout.endpoints).filter(ep => ep.meta?.[key] === value);
    },
    /**
     * Get the global position of an endpoint, resolving parent chain.
     * If endpoint has a parent, adds parent's position recursively.
     */
    getGlobalPosition(layout, id) {
        const endpoint = layout.endpoints[id];
        if (!endpoint)
            return undefined;
        if (!endpoint.parentId) {
            return endpoint.position;
        }
        const parentPos = LayoutQueries.getGlobalPosition(layout, endpoint.parentId);
        if (!parentPos) {
            return endpoint.position;
        }
        return {
            x: parentPos.x + endpoint.position.x,
            y: parentPos.y + endpoint.position.y,
        };
    },
    // ===========================================
    // BLACKJACK-SPECIFIC QUERIES
    // ===========================================
    /**
     * Get the kitty endpoint for a visual seat position.
     */
    getKitty(layout, visualPos) {
        return layout.endpoints[BlackjackEndpointIds.kitty(visualPos)];
    },
    /**
     * Get the tray chip endpoint for a denomination.
     */
    getTrayChip(layout, denomination) {
        return layout.endpoints[BlackjackEndpointIds.trayChip(denomination)];
    },
    /**
     * Get the rail chip endpoint for a denomination.
     */
    getRailChip(layout, denomination) {
        return layout.endpoints[BlackjackEndpointIds.railChip(denomination)];
    },
    /**
     * Get the staged bet endpoint.
     */
    getStagedBet(layout) {
        return layout.endpoints[BlackjackEndpointIds.stagedBet];
    },
};
// ===========================================
// LAYOUT MUTATION (FOR DEV CONTROLS)
// ===========================================
/**
 * Update an endpoint's position. Returns a new layout with incremented version.
 * Used by DEV CONTROLS to modify endpoints in real-time.
 */
export function updateEndpointPosition(layout, endpointId, position, updatedBy) {
    const endpoint = layout.endpoints[endpointId];
    if (!endpoint)
        return layout;
    return {
        ...layout,
        version: layout.version + 1,
        endpoints: {
            ...layout.endpoints,
            [endpointId]: {
                ...endpoint,
                position: {
                    x: position.x ?? endpoint.position.x,
                    y: position.y ?? endpoint.position.y,
                },
            },
        },
        meta: {
            ...layout.meta,
            updatedAt: Date.now(),
            updatedBy,
        },
    };
}
/**
 * Update an endpoint's scale. Returns a new layout with incremented version.
 */
export function updateEndpointScale(layout, endpointId, scale, updatedBy) {
    const endpoint = layout.endpoints[endpointId];
    if (!endpoint)
        return layout;
    return {
        ...layout,
        version: layout.version + 1,
        endpoints: {
            ...layout.endpoints,
            [endpointId]: {
                ...endpoint,
                scale,
            },
        },
        meta: {
            ...layout.meta,
            updatedAt: Date.now(),
            updatedBy,
        },
    };
}
/**
 * Batch update multiple endpoints. Returns a new layout with incremented version.
 * More efficient than calling individual updates.
 */
export function updateEndpoints(layout, updates, updatedBy) {
    const newEndpoints = { ...layout.endpoints };
    for (const update of updates) {
        const endpoint = newEndpoints[update.id];
        if (!endpoint)
            continue;
        newEndpoints[update.id] = {
            ...endpoint,
            position: update.position ? {
                x: update.position.x ?? endpoint.position.x,
                y: update.position.y ?? endpoint.position.y,
            } : endpoint.position,
            scale: update.scale ?? endpoint.scale,
            rotation: update.rotation ?? endpoint.rotation,
        };
    }
    return {
        ...layout,
        version: layout.version + 1,
        endpoints: newEndpoints,
        meta: {
            ...layout.meta,
            updatedAt: Date.now(),
            updatedBy,
        },
    };
}
// ===========================================
// DEFAULT BLACKJACK LAYOUT
// ===========================================
/**
 * Standard chip denominations for Blackjack.
 */
export const BLACKJACK_DENOMINATIONS = [1, 5, 25, 100, 500, 1000, 5000, 10000];
/**
 * Default Blackjack layout configuration.
 * Based on existing PixiGameTableV1 positions.
 */
export const DEFAULT_BLACKJACK_LAYOUT_CONFIG = {
    seatCount: 7,
    // Seat positions will be populated from dynamicSeatPositions
    // These are placeholder values - actual positions come from polar calculations
    seatPositions: {
        1: { x: 960, y: 690, rotation: 0 }, // Bottom center (current user)
        2: { x: 1152, y: 630, rotation: -30 },
        3: { x: 1310, y: 510, rotation: -60 },
        4: { x: 1380, y: 350, rotation: -90 },
        5: { x: 540, y: 350, rotation: 90 },
        6: { x: 610, y: 510, rotation: 60 },
        7: { x: 768, y: 630, rotation: 30 },
    },
    // Kitty is above each seat
    kittyOffset: { x: 0, y: -85 },
    kittyScale: 1,
    // Insurance is to the side of each seat
    insuranceOffset: { x: 80, y: -20 },
    insuranceScale: 0.8,
    // Tray position (dealer's chip tray)
    trayPosition: { x: 1150, y: 100 },
    trayRotation: 0,
    trayScale: 1,
    trayChipPositions: {
        1: { x: -55, y: 35 },
        5: { x: -40, y: 40 },
        25: { x: -20, y: 45 },
        100: { x: 0, y: 48 },
        500: { x: 20, y: 45 },
        1000: { x: 35, y: 40 },
        5000: { x: 48, y: 35 },
        10000: { x: 55, y: 30 },
    },
    // Rail position (player's clickable chips)
    railPosition: { x: 960, y: 1000 },
    railScale: 1,
    railChipPositions: {
        1: { x: -297, y: 0 },
        5: { x: -212, y: -20 },
        25: { x: -127, y: -30 },
        100: { x: -42, y: -35 },
        500: { x: 42, y: -35 },
        1000: { x: 127, y: -30 },
        5000: { x: 212, y: -20 },
        10000: { x: 297, y: 0 },
    },
    // Staged bet position
    stagedBetPosition: { x: 960, y: 880 },
    stagedBetScale: 1,
    // Shoe position
    shoePosition: { x: 680, y: 80 },
    shoeRotation: 90,
    shoeScale: 1.4,
    // Dealer position
    dealerPosition: { x: 960, y: 130 },
};
/**
 * Create the default Blackjack table layout.
 */
export function createDefaultBlackjackLayout() {
    return createBlackjackLayout(DEFAULT_BLACKJACK_LAYOUT_CONFIG);
}
//# sourceMappingURL=table-layout.js.map